import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-black uppercase mb-2">The Vault</h1>
          <p className="text-sm text-red-600">Invalid reset link. No token found.</p>
          <a href="/" className="text-sm text-gray-500 hover:text-gray-900 mt-4 inline-block">Back to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-black uppercase mb-1">The Vault</h1>
        <div className="h-px w-12 bg-black/20 mb-6"></div>

        {success ? (
          <div className="p-4 border border-green-200 bg-green-50 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-green-600" strokeWidth={2} />
              <p className="text-sm font-medium text-green-800">Password reset successful</p>
            </div>
            <a href="/" data-testid="back-to-login" className="text-sm text-green-700 underline">Sign in with your new password</a>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-xl font-semibold tracking-tight text-gray-900 mb-1">Set new password</h2>
            <p className="text-sm text-gray-500 mb-6">Enter and confirm your new password.</p>

            {error && (
              <div data-testid="reset-error" className="mb-4 p-3 border border-red-200 bg-red-50 rounded-sm text-sm text-red-700">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="new-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 rounded-sm" placeholder="Min 6 characters" required />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                  <Input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10 rounded-sm" placeholder="Repeat password" required />
                </div>
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={submitting} className="w-full rounded-sm bg-black text-white hover:bg-gray-800 gap-2">
                {submitting ? "Resetting..." : "Reset Password"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
